export type LayoutPanels = {
	layoutName: string
	panels: Panel[]
}

export type Panel = {
	label: string
	formKey: string
	type: PanelType
}

export type PanelType =
	| 'baseContainerPanel'
	| 'containerPanel'
	| 'textPanel'
	| 'imagePanel'
	| 'imageContainerPanel'
	| 'buttonsContainerPanel'
	| 'carouselPanel'

export type Animation = {
	enable: boolean
	enableExit: boolean
	type: string
	ease: string
	duration: number
	delay: number
}

export const defaultAnimation: Animation = {
	enable: false,
	enableExit: false,
	type: 'fade',
	ease: 'ease',
	duration: 1000,
	delay: 0,
}

type Button = {
	visible: boolean
	text: string
	textColor: string
	backgroundColor: string
	design: 'simple' | 'minimal' | 'default'
	action: 'navigate'
	link: string
}

export type ButtonsContainer = {
	container: { placement: 'left' | 'center' | 'right' }
	primary: Button
	secondary: Button
	animation: Animation
}

export const defaultButtonsContainer: ButtonsContainer = {
	container: {
		placement: 'center',
	},

	primary: {
		visible: false,
		text: 'Bliv medlem',
		textColor: '#ffffff',
		backgroundColor: '#EF8F49',
		design: 'default',
		action: 'navigate',
		link: '',
	},

	secondary: {
		visible: false,
		text: 'Læs mere',
		textColor: '#EF8F49',
		backgroundColor: '#EF8F49',
		design: 'simple',
		action: 'navigate',
		link: '',
	},

	animation: defaultAnimation,
}

export type Text = {
	text: string
	color: string
	isBold: boolean
	isItalic: boolean
	isUnderlined: boolean
	textAlign: 'left' | 'center' | 'right'
	fontFamily: "'Montserrat Variable', sans-serif" | "'Poppins', sans-serif"
	htmlTag: 'h1' | 'h2' | 'h3' | 'h4' | 'p'
	animation: Animation
}

export type ImageContainer = {
	source: string
	alt: string
	placement: 'right' | 'left'
	style: 'cover' | 'float'
	link: string
	animation: Animation
}

export type Image = {
	source: string
	alt: string
	text?: string
	textColor?: string
	link: string
	fit: 'cover' | 'contain'
	shadow: boolean
	animation: Animation
}

export type BaseContainer = {
	fullscreen: boolean
	backgroundColor: string
	coverSource?: string
	coverFit?: 'cover' | 'contain'
	coverPosition?: string
	animation: Animation
}

export type Carousel = {
	category: number
}

export const defaultBaseContainer: BaseContainer = {
	fullscreen: false,
	backgroundColor: '#fffdfa',
	coverSource: '',
	coverFit: 'cover',
	coverPosition: 'center',
	animation: defaultAnimation,
}

export type Container = {
	backgroundColor: string
	backgroundOpacity: number
	backgroundBlur: number
	borderColor: string
	borderWidth: number
	showBorder: boolean
	borderRadius: number
	animation: Animation
}

export const defaultContainer: Container = {
	backgroundColor: '#fffdfa',
	backgroundOpacity: 0,
	backgroundBlur: 0,
	borderColor: '#eeeeee',
	borderWidth: 6,
	showBorder: false,
	borderRadius: 0,
	animation: defaultAnimation,
}

export type LayoutValues = {
	[sectionName: string]: Container | BaseContainer | ButtonsContainer | Text | Image | ImageContainer | Carousel
}

export type Layouts = {
	[layoutName: string]: {
		panel: LayoutPanels
		Component: (props: any) => JSX.Element
		values: LayoutValues
	}
}

// att:: animations hides content instead of revealing
export const getAnimationProps = (animation: Animation) => {
	return {}
	// if (animation.enable === false) return {};

	// const animationProps = {
	// 	'data-aos': animation.type,
	// 	'data-aos-delay': animation.delay,
	// 	'data-aos-duration': animation.duration,
	// 	'data-aos-easing': animation.ease,
	// 	'data-aos-mirror': animation.enableExit,
	// 	'data-aos-once': false,
	// };

	// return animationProps;
}

export const formatText = (text: string) => {
	const linkPattern = /\[([\w ]+)\]\((.+)\)/gi
	const emailPattern = /\[([\w ]+)\]\(([\w\-._+]+@[\w\-_]+\.[\w]{2,3})\)/gi

	text = text.replace(linkPattern, (match, text, href) => {
		return `<a href="${href}">${text}</a>`
	})

	text = text.replace(emailPattern, (match, text, mail) => {
		return `<a href="mailto:${mail}">${text}</a>`
	})

	return text
}

export const getTextStyle = (context: Text) => `
	width: 100%;
	color: ${context.color};
	text-align: ${context.textAlign};
	font-family: ${context.fontFamily};
	text-decoration: ${context.isUnderlined ? 'underline' : 'none'};
	font-style: ${context.isItalic ? 'italic' : 'normal'};
	font-weight: ${context.isBold ? 'bold' : 'normal'};
`

export const getContainerStyle = (context: Container) => {
	const backgroundOpacity = Number(context.backgroundOpacity).toString(16)

	return `
		width: 100%;
		border-style: ${context.showBorder ? 'solid' : 'none'};
		border-radius: ${context.borderRadius}px;
		border-width: ${context.borderWidth}px;
		border-color: ${context.borderColor};
		backdrop-filter: blur(${context.backgroundBlur}px);
		background-color: ${context.backgroundColor + (backgroundOpacity === '0' ? '00' : backgroundOpacity)};
	`
}

export const getImageSource = (imageSource: string | null = null) => {
	const imageSourceHost = 'https://filer.oplevalt.dk/cms/large_'
	return imageSourceHost + imageSource
}
